// @flow

import _ from 'lodash';
import type { ThemeSetupV3State, ThemeSetupV3Action } from 'stores/flow-type/theme-setup.type';
import { capitalizeFirstLetter } from 'src/helpers';

import {
  GET_THEMES_REQUEST,
  GET_THEMES_SUCCESS,
  GET_THEMES_FAILURE,
  GET_ALL_THEMES_V3_REQUEST,
  GET_ALL_THEMES_V3_SUCCESS,
  GET_ALL_THEMES_V3_FAILURE,
  CHECK_THEME_V3_REQUEST,
  CHECK_THEME_V3_SUCCESS,
  CHECK_THEME_V3_FAILURE,
  SETUP_THEME_V3_REQUEST,
  SETUP_THEME_V3_SUCCESS,
  SETUP_THEME_V3_FAILURE,
  REVERT_THEME_V3_REQUEST,
  REVERT_THEME_V3_SUCCESS,
  REVERT_THEME_V3_FAILURE,
  REMOVE_OLD_THEME_REQUEST,
  REMOVE_OLD_THEME_SUCCESS,
  REMOVE_OLD_THEME_FAILURE,
  UPGRADE_THEME_V3_REQUEST,
  UPGRADE_THEME_V3_SUCCESS,
  UPGRADE_THEME_V3_FAILURE,
  RESET_THEME_SETUP_V3,
  GET_MAIN_THEME_SETUP_STATUS_REQUEST,
  GET_MAIN_THEME_SETUP_STATUS_FAILURE,
  GET_MAIN_THEME_SETUP_STATUS_SUCCESS
} from 'stores/flow-type/theme-setup-v3.type';

const initialState: ThemeSetupV3State = {
  getThemesLoading: false,
  getAllThemesLoading: false,
  checkThemeLoading: false,
  setupThemeLoading: false,
  submitErrorLoading: false,
  integrateGemPagesLoading: false,
  upgradeThemeLoading: false,
  getThemesSuccess: false,
  getAllThemesSuccess: false,
  checkThemeSuccess: false,
  setupThemeSuccess: undefined,
  submitErrorSuccess: false,
  integrateGemPagesSuccess: false,
  upgradeThemeSuccess: false,
  error: undefined,
  setupThemeError: undefined,
  themes: [],
  allThemes: [],
  isTrialTheme: false,
  inputThemeName: '',
  outputThemeName: '',
  isOS20: false,
  integrateGemPagesThemes: {},
  mainThemeSetupStatus: 'loading',
  removeFeatures: []
};

const themeSetupV3Reducers = (
  state: ThemeSetupV3State = initialState,
  action: ThemeSetupV3Action
) => {
  switch (action.type) {
    case GET_ALL_THEMES_V3_REQUEST:
      return { ...state, getAllThemesLoading: true, getAllThemesSuccess: false };
    case GET_ALL_THEMES_V3_SUCCESS: {
      const { data = [] } = action.payload;
      return {
        ...state,
        getAllThemesLoading: false,
        getAllThemesSuccess: true,
        error: undefined,
        allThemes: data
      };
    }
    case GET_ALL_THEMES_V3_FAILURE:
      return {
        ...state,
        getAllThemesLoading: false,
        getAllThemesSuccess: false,
        error: action.payload
      };

    case CHECK_THEME_V3_REQUEST:
      return {
        ...state,
        checkThemeLoading: true,
        checkThemeSuccess: false,
        isTrialTheme: false,
        inputThemeName: '',
        outputThemeName: '',
        themeCheck: null,
        isOS20: false
      };
    case CHECK_THEME_V3_SUCCESS: {
      const {
        data: { isTrialTheme, inputThemeName, outputThemeName, isOS20 }
      } = action.payload;
      const themeCheck = _.get(action, 'payload.data', null);
      const themeCheckId = _.get(action, 'payload.themeId', null);
      return {
        ...state,
        checkThemeLoading: false,
        checkThemeSuccess: true,
        isTrialTheme,
        inputThemeName,
        outputThemeName,
        isOS20,
        themeCheck,
        themeCheckId,
        error: undefined
      };
    }
    case CHECK_THEME_V3_FAILURE:
      return {
        ...state,
        checkThemeLoading: false,
        checkThemeSuccess: false,
        isOS20: false,
        error: action.payload
      };

    case SETUP_THEME_V3_REQUEST:
      // use `setupThemeSuccess: undefined` for case retry
      return {
        ...state,
        setupThemeLoading: true,
        setupThemeSuccess: undefined
      };
    case SETUP_THEME_V3_SUCCESS: {
      return {
        ...state,
        setupThemeLoading: false,
        setupThemeSuccess: true,
        setupThemeError: undefined
      };
    }
    case SETUP_THEME_V3_FAILURE:
      return {
        ...state,
        setupThemeLoading: false,
        setupThemeSuccess: false,
        setupThemeError: action.payload
      };

    case GET_THEMES_REQUEST:
      return { ...state, getThemesLoading: true, getThemesSuccess: false };
    case GET_THEMES_SUCCESS: {
      const { data } = action.payload;
      const existedThemeLibV2 = data.findIndex(({ themeLibV2 }) => themeLibV2) !== -1;
      return {
        ...state,
        getThemesLoading: false,
        getThemesSuccess: true,
        error: undefined,
        themes: data,
        existedThemeLibV2
      };
    }
    case GET_THEMES_FAILURE:
      return {
        ...state,
        getThemesLoading: false,
        getThemesSuccess: false,
        error: action.payload
      };

    case REVERT_THEME_V3_REQUEST:
      return {
        ...state,
        revertThemeloading: true,
        revertThemeSuccess: undefined,
        revertThemeError: undefined
      };
    case REVERT_THEME_V3_SUCCESS: {
      return {
        ...state,
        revertThemeloading: false,
        revertThemeSuccess: true,
        revertThemeError: undefined,
        revertThemeV3ShowModalSurvey: true,
        revertTheme: _.get(action, 'payload.theme', {}),
        removeFeatures: _.get(action, 'payload.removeFeatures', []).map(featureKey =>
          capitalizeFirstLetter(featureKey, 'en')
        ),
        error: undefined
      };
    }
    case REVERT_THEME_V3_FAILURE: {
      const { themeId } = action.payload;
      return {
        ...state,
        revertThemeloading: false,
        revertThemeSuccess: false,
        revertThemeId: themeId,
        revertThemeV3ShowModalSurvey: true,
        revertThemeError: action.payload,
        error: action.payload
      };
    }

    case REMOVE_OLD_THEME_REQUEST:
      return {
        ...state,
        removeOldThemeloading: true,
        removeOldThemeSuccess: undefined,
        removeOldThemeError: undefined
      };
    case REMOVE_OLD_THEME_SUCCESS: {
      const themeCheck = _.get(state, 'themeCheck', {});
      themeCheck.taeFeatures = null;
      themeCheck.feLibVersion = null;
      themeCheck.enabledBoostCoreV3 = false;

      return {
        ...state,
        themeCheck,
        removeOldThemeloading: false,
        removeOldThemeSuccess: true,
        removeOldThemeError: undefined,
        removeOldTheme: _.get(action, 'payload.theme', {}),
        error: undefined
      };
    }
    case REMOVE_OLD_THEME_FAILURE: {
      const { themeId: themeIdRemove } = action.payload;
      return {
        ...state,
        removeOldThemeloading: false,
        removeOldThemeSuccess: false,
        removeOldThemeId: themeIdRemove,
        removeOldThemeError: action.payload,
        error: action.payload
      };
    }

    case UPGRADE_THEME_V3_REQUEST:
      return {
        ...state,
        upgradeThemeloading: true,
        upgradeThemeSuccess: false,
        upgradeThemeError: undefined
      };
    case UPGRADE_THEME_V3_SUCCESS: {
      return {
        ...state,
        upgradeThemeloading: false,
        upgradeThemeSuccess: true,
        upgradeThemeError: undefined,
        upgradeTheme: _.get(action, 'payload.theme', {}),
        error: undefined
      };
    }
    case UPGRADE_THEME_V3_FAILURE: {
      const { themeId: id } = action.payload;
      return {
        ...state,
        upgradeThemeloading: false,
        upgradeThemeSuccess: false,
        upgradeThemeId: id,
        upgradeThemeError: action.payload,
        error: action.payload
      };
    }

    case RESET_THEME_SETUP_V3:
      return initialState;

    case GET_MAIN_THEME_SETUP_STATUS_REQUEST:
      return { ...state };
    case GET_MAIN_THEME_SETUP_STATUS_SUCCESS: {
      const { status = 'noTheme' } = action.payload;
      return {
        ...state,
        mainThemeSetupStatus: status
      };
    }
    case GET_MAIN_THEME_SETUP_STATUS_FAILURE:
      return {
        ...state,
        mainThemeSetupStatus: 'noTheme'
      };

    default:
      return state;
  }
};
export default themeSetupV3Reducers;
